// src/pages/Review.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db, submitExhibit, updateExhibit } from "../firebase.config";
import { useForm } from "react-hook-form";
import { doc, onSnapshot } from "firebase/firestore";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../Redux/Loader/loadingSlice";
import { FaExclamationCircle } from "react-icons/fa";
import { sendSubmissionEmail } from "../utils/email";
import { downloadTestLeaseModel } from "../utils/leaseModel";

function cacheBust(url, updatedAt) {
  if (!url) return null;
  let v = Date.now();
  if (updatedAt?.toMillis) v = updatedAt.toMillis();
  else if (updatedAt?.seconds) v = updatedAt.seconds * 1000;
  else if (typeof updatedAt === "number") v = updatedAt;
  else if (typeof updatedAt === "string")
    v = Date.parse(updatedAt) || Date.now();
  return `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(v)}`;
}

const Review = ({ reviewExhibitID }) => {
  const [pdfURL, setPdfURL] = useState(null);
  const [imgURLs, setImgURLs] = useState([]);
  const [exhibitData, setExhibitData] = useState(null);
  const user = useSelector((state) => state.user);
  // RHF: we use it only for the agree checkbox + error
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onSubmit" });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [submitting, setSubmitting] = useState(false);

  // Prefer exhibit id passed via Link state; fallback to prop
  const exhibitId = location.state?.exhibitId ?? reviewExhibitID ?? null;

  // Decide whether to show the submit section
  const shouldShowSubmit = useMemo(() => {
    if (typeof location.state?.showSubmit === "boolean")
      return location.state.showSubmit;
    if (location.state?.fromSaveContinue) return true;
    return location.pathname.endsWith("/review");
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (!auth.currentUser) navigate("/");
  }, [navigate]);

  // Seed with cache-busted URL passed from Dashboard (fast, no flash of stale)
  useEffect(() => {
    if (location.state?.pdfHref) {
      setPdfURL(location.state.pdfHref);
    }
  }, [location.state?.pdfHref]);

  // Live subscribe to the exhibit
  useEffect(() => {
    if (!exhibitId) return;
    dispatch(setLoading(true));

    const unsub = onSnapshot(
      doc(db, "exhibits", exhibitId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setExhibitData({ id: exhibitId, ...data });

          const storedPdf = data.pdfURL || data.pdfURL || null; // supports old/new key
          const fresh = cacheBust(storedPdf, data.updatedAt);
          setPdfURL((prev) => fresh || prev || null);

          setImgURLs(Array.isArray(data.fileUrls) ? data.fileUrls : []);
        } else {
          setExhibitData(null);
          setPdfURL(null);
          setImgURLs([]);
          console.warn("No such document:", `exhibits/${exhibitId}`);
        }
        dispatch(setLoading(false));
      },
      (err) => {
        console.error("Error fetching exhibit data:", err);
        dispatch(setLoading(false));
      }
    );

    return () => unsub();
  }, [dispatch, exhibitId]);

  // RHF-powered submit (agree is guaranteed true here if it runs)
  const handleSubmitExhibit = async () => {
    if (!exhibitId) return;

    try {
      setSubmitting(true);

      // 1) Mark as submitted in Firestore
      await submitExhibit(exhibitId);

      // 2) Send the email with PDF + image URLs
      if (exhibitData) {
        try {
          // await downloadTestLeaseModel(exhibitData);
          await sendSubmissionEmail(
            {
              ...exhibitData,
              id: exhibitId,
              status: "Submitted",
              pdfURL,
              imgURLs,
            },
            user
          );
        } catch (emailErr) {
          console.error("[Review] email send failed:", emailErr);
          // still let the user proceed
        }
      } else {
        console.warn("[Review] No exhibitData in state; skipping email send.");
      }

      // 3) Notify user + redirect
      alert(
        "Your exhibit has been submitted to MLinc Solutions for valuation."
      );
      navigate("/dashboard", { replace: true });
    } catch (e) {
      console.error("[Review] submit failed:", e);
    } finally {
      setSubmitting(false);
    }
  };

  // Scroll to top on mount (esp. from Save & Continue)
  useEffect(() => {
    try {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: location.state?.fromSaveContinue ? "smooth" : "auto",
      });
    } catch {
      window.scrollTo(0, 0);
    }
    const scrollers = document.querySelectorAll(
      ".review-container, .inner-rev-container, .pdf-viewer-container"
    );
    scrollers.forEach((el) => {
      if (el?.scrollTo) el.scrollTo({ top: 0, left: 0 });
      else if (el) {
        el.scrollTop = 0;
        el.scrollLeft = 0;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="review-container">
      <h2 className="review-h2">Review Your Submission</h2>
      <div className="inner-rev-container">
        <div className="pdf-viewer-container">
          {pdfURL ? (
            <iframe
              key={pdfURL}
              src={pdfURL}
              width={1000}
              height={950}
              title="PDF Viewer"
            />
          ) : (
            <div className="no-pdf">No PDF available yet.</div>
          )}

          {shouldShowSubmit ? (
            <form
              className="submit-review-form"
              onSubmit={handleSubmit(handleSubmitExhibit)}
              noValidate
              autoComplete="off"
            >
              <div
                className={
                  errors.agree
                    ? "job-form-input-container error"
                    : "job-form-input-container"
                }
              >
                {errors.agree && (
                  <p className="error-message" role="alert">
                    <span style={{ marginRight: 5 }}>
                      <FaExclamationCircle />
                    </span>
                    {errors.agree.message}
                  </p>
                )}

                <div className="agree-container">
                  <div className="agree-row">
                    <input
                      id="agree"
                      type="checkbox"
                      aria-invalid={!!errors.agree}
                      aria-describedby={
                        errors.agree ? "agree-error" : undefined
                      }
                      {...register("agree", {
                        required:
                          "Please check the box to confirm before submitting.",
                      })}
                    />
                    <label htmlFor="agree">
                      I certify the information is accurate and ready for
                      submission
                    </label>
                  </div>

                  <p
                    className="terms-p small"
                    id="agree-help"
                    style={{ marginTop: 6 }}
                  >
                    By checking this box, you are electronically signing your
                    order and authorizing us to use a prepaid credit or charge
                    payments against the credit card on file with MLinc Mortgage
                    Solutions.
                  </p>
                </div>
              </div>

              <div className="button-container">
                <button
                  className="retrn-btn submit-btn exhibit-save"
                  type="button"
                  onClick={async () => {
                    if (!exhibitId) {
                      navigate("/dashboard");
                      return;
                    }
                    try {
                      dispatch(setLoading(true));
                      //load the current exhibit data and update status only
                      await updateExhibit({ id: exhibitId, status: "Saved" });
                    } catch (e) {
                      console.error("[Review] Save & Exit failed: ", e);
                    } finally {
                      dispatch(setLoading(false));
                      navigate("/dashboard", {
                        replace: true,
                        state: { fromSave: true },
                      });
                    }
                  }}
                >
                  Save & Exit
                </button>
                <button
                  className="submit-btn exhibit-submit"
                  type="submit"
                  disabled={submitting}
                  aria-disabled={submitting}
                  title="Submit"
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          ) : (
            <button
              className="retrn-btn submit-btn exhibit-submit"
              type="button"
              onClick={() => navigate("/dashboard")}
            >
              Return to Dashboard
            </button>
          )}
        </div>

        {imgURLs.length > 0 && (
          <div className="image-review-container">
            <h3>Images</h3>
            <div className="image-review">
              {imgURLs.map((imgURL, index) => (
                <img
                  key={index}
                  src={imgURL}
                  alt={`Exhibit Image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Review;
